import { Command, Option } from "clipanion";
import { DefaultAzureCredential, ManagedIdentityCredential, type TokenCredential } from "@azure/identity";
import {
  applyRequestToEntity, blocksNewIntent, newIntent, type ApplyRequest,
} from "@m8t-stack/platform-release";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { isTableAccessDenied, openApplyTable, readApplyRequest } from "../../lib/apply-request-store.js";
import { classifyPolledRow, outcomeExitCode, outcomeStatus } from "../../lib/request-update-follow.js";
import { renderJson, resolveOutputMode } from "../../lib/output.js";

export class PlatformRequestUpdateCommand extends M8tCommand {
  static paths = [["platform", "request-update"]];
  static usage = Command.Usage({
    description: "Ask this installation's updater to converge to a version, exactly as the in-app button does.",
    details:
      "Writes the update request the updater job claims on its next tick. This is the ONLY privilege the requester holds — it does not deploy anything itself; the install's own updater fetches the release, applies it, health-gates it and rolls back on failure. With --wait, follows the request to a terminal state and exits non-zero if it did not succeed.\n\n" +
      "The request is a row in the install's Metadata table, so the signed-in identity needs a table data role (Storage Table Data Contributor) on the install's storage account; Owner alone is not enough. Without one, use \"Update now\" in the app, which writes the same request with the gateway's identity. See docs/operations/request-a-platform-update.md.",
    examples: [
      ["Request an update", "m8t platform request-update --version 0.7.5 --resource-group rg-m8t --subscription <id>"],
      ["Request it and wait", "m8t platform request-update --version 0.7.5 --wait --resource-group rg-m8t --subscription <id>"],
    ],
  });

  version = Option.String("--version", { description: "The platform version to converge to." });
  subscription = Option.String("--subscription");
  resourceGroup = Option.String("--resource-group");
  source = Option.String("--source", "cd", { description: "Who is asking: cd | button | policy." });
  identity = Option.String("--identity", "", { description: "Recorded on the request, for the audit trail." });
  miClientId = Option.String("--mi-client-id");
  wait = Option.Boolean("--wait", false, { description: "Follow the request until it settles." });
  /**
   * Follow an EXISTING request without writing a new one.
   *
   * Lets a caller write the request in one step and wait in another, which is
   * what makes the ordering provable: the row is a fact before anything is
   * started against it, instead of a background write racing a sleep.
   */
  waitOnly = Option.Boolean("--wait-only", false, { description: "Follow the current request without creating one." });
  timeoutMinutes = Option.String("--timeout-minutes", "45");
  pollSeconds = Option.String("--poll-seconds", "20");
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(this.output as "pretty" | "json" | "auto" | undefined, this.context.stdout as NodeJS.WriteStream);
    const need = (v: unknown, flag: string): string => {
      if (typeof v !== "string" || v.trim().length === 0) {
        throw new LocalCliError({ code: "PLATFORM_REQUEST_UPDATE_ARG_MISSING", message: `${flag} is required.` });
      }
      return v.trim();
    };
    const target = need(this.version, "--version");
    // Validated off the RAW string. Casting first and then comparing narrows the
    // type to the union, which makes the check provably dead — it would compile,
    // lint would call the last branch unreachable, and an invalid --source would
    // reach the row as a value nothing downstream expects.
    const SOURCES: readonly string[] = ["cd", "button", "policy"];
    if (!SOURCES.includes(this.source)) {
      throw new LocalCliError({ code: "PLATFORM_REQUEST_UPDATE_BAD_SOURCE", message: `--source must be cd, button or policy; got '${this.source}'.` });
    }
    const source = this.source as ApplyRequest["provenance"]["source"];

    const credential: TokenCredential =
      typeof this.miClientId === "string" && this.miClientId.trim().length > 0
        ? new ManagedIdentityCredential({ clientId: this.miClientId.trim() })
        : new DefaultAzureCredential();

    const client = await openApplyTable({
      credential,
      subscriptionId: need(this.subscription, "--subscription"),
      resourceGroup: need(this.resourceGroup, "--resource-group"),
    });

    try {
      return await this.requestAndFollow(client, target, source, mode);
    } catch (e) {
      if (!isTableAccessDenied(e)) throw e;
      // Owner on the subscription is control plane only; the request row is table data.
      throw new LocalCliError({
        code: "PLATFORM_REQUEST_UPDATE_FORBIDDEN",
        message:
          "This Azure identity was refused the install's update request, which is table data on the install's storage account. Writing it needs a table data role there (Storage Table Data Contributor); control-plane roles such as Owner don't include one.",
        hint:
          `Use the install's own update route, which writes the same request with the gateway's identity: "Update now" in the app, or POST https://<gateway>/api/platform/apply with {"target":"${target}"} as a signed-in user. The updater job claims it on its next tick, or start it now with az containerapp job start. Granting this identity Storage Table Data Contributor on that account is the install owner's decision. See docs/operations/request-a-platform-update.md.`,
        cause: e,
      });
    }
  }

  private async requestAndFollow(
    client: Awaited<ReturnType<typeof openApplyTable>>,
    target: string,
    source: ApplyRequest["provenance"]["source"],
    mode: ReturnType<typeof resolveOutputMode>,
  ): Promise<number> {
    // Same collision rule the in-app button obeys: never overwrite a request the
    // updater may be acting on. A caller that finds one in flight is told so
    // rather than silently replacing it — two requests racing over one
    // deployment is how a half-applied platform happens.
    const log = (m: string): void => { this.context.stderr.write(`${m}\n`); };

    if (!this.waitOnly) {
      const existing = await readApplyRequest(client);
      if (existing && blocksNewIntent(existing.status)) {
        throw new LocalCliError({
          code: "PLATFORM_REQUEST_UPDATE_IN_PROGRESS",
          message: `an update request for ${existing.target} is already ${existing.status}.`,
          hint: "Wait for it to settle, or clear it with `m8t platform clear-intent` if nothing is advancing it.",
        });
      }

      const now = new Date().toISOString();
      const intent = newIntent(target, { source, identity: this.identity || source, at: now }, now);
      await client.upsertEntity(applyRequestToEntity(intent), "Replace");
      log(`requested update to ${target} (source ${source}).`);
    } else {
      log(`following the existing request for ${target}.`);
    }

    if (!this.wait && !this.waitOnly) {
      this.context.stdout.write(
        mode === "json" ? renderJson({ requested: true, target, waited: false }) + "\n" : `requested update to ${target}.\n`,
      );
      return 0;
    }

    const settled = await this.follow(client, target, log);

    const result = {
      requested: true,
      target,
      waited: true,
      status: outcomeStatus(settled),
      error: settled?.result?.error ?? null,
    };
    this.context.stdout.write(
      mode === "json" ? renderJson(result) + "\n" : `${target}: ${result.status}${result.error ? ` — ${result.error}` : ""}\n`,
    );
    return outcomeExitCode(settled);
  }

  /**
   * Follow the request until it settles.
   *
   * `awaiting-engine-update` is explicitly NOT a failure and not a stall: the
   * updater parks a request there while it replaces its own image, so a converge
   * legitimately spans two ticks. Treating that as stuck would fail a build for
   * doing exactly what it is supposed to do.
   */
  private async follow(
    client: Awaited<ReturnType<typeof openApplyTable>>,
    target: string,
    log: (m: string) => void,
  ): Promise<ApplyRequest | null> {
    const timeoutMs = Number(this.timeoutMinutes) * 60 * 1000;
    const pollMs = Number(this.pollSeconds) * 1000;
    if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
      throw new LocalCliError({ code: "PLATFORM_REQUEST_UPDATE_BAD_TIMEOUT", message: `--timeout-minutes must be a positive number, got '${this.timeoutMinutes}'.` });
    }
    const deadline = Date.now() + timeoutMs;
    let lastReported = "";
    let consecutiveAbsent = 0;

    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, pollMs));
      const verdict = classifyPolledRow(await readApplyRequest(client), target);

      // No row at all. In the CD flow the request step guarantees one exists, so
      // a persistent absence is a real error — waiting the full timeout to say
      // so turns a one-minute failure into a forty-minute one. A few polls of
      // grace covers a transient read.
      if (!verdict) {
        consecutiveAbsent += 1;
        if (consecutiveAbsent >= 3) {
          throw new LocalCliError({
            code: "PLATFORM_REQUEST_UPDATE_ROW_ABSENT",
            message: `no update request found for ${target} after ${String(consecutiveAbsent)} polls.`,
            hint: "Something removed the row after it was written. Check for a concurrent `clear-intent` or a competing requester.",
          });
        }
        continue;
      }
      consecutiveAbsent = 0;

      if (verdict.kind === "settled") return verdict.row;

      // Deduped: a persistently-mismatched row, or a converge sitting in one
      // phase, would otherwise emit a line every poll — ~120 of them across a
      // 40-minute wait, burying the transitions that actually mean something.
      const line = verdict.kind === "not-ours"
        ? `  (ignoring a request for ${verdict.otherTarget} — not the one we made)`
        : `  ${verdict.where}`;
      if (line !== lastReported) {
        log(line);
        lastReported = line;
      }
    }
    return null;
  }
}
